import type { Platform } from './types';

// Minimal typing of the parts of Telegram.WebApp we use.
interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { start_param?: string; user?: { language_code?: string } };
  platform: string;
  ready(): void;
  expand(): void;
  setHeaderColor?(color: string): void;
  BackButton: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void };
  HapticFeedback?: { impactOccurred(s: string): void; notificationOccurred(s: string): void };
  openLink(url: string, options?: { try_instant_view?: boolean }): void;
  requestContact?(cb: (shared: boolean) => void): void;
  requestWriteAccess?(cb: (allowed: boolean) => void): void;
  isVersionAtLeast?(v: string): boolean;
}

export function telegramWebApp(): TelegramWebApp | null {
  const app = (window as unknown as { Telegram?: { WebApp?: TelegramWebApp } }).Telegram?.WebApp;
  return app && app.initData ? app : null;
}

/** startapp payloads: "trip_EMABC123", "listing_42" → app routes. */
export function startParamToRoute(param: string | undefined): string | null {
  if (!param) return null;
  const [kind, value] = param.split('_');
  if (kind === 'trip' && value) return `/trips/${value}`;
  if (kind === 'listing' && value) return `/listings/${value}`;
  return null;
}

export function createTelegramPlatform(app: TelegramWebApp): Platform {
  let backHandler: (() => void) | null = null;
  return {
    name: 'telegram',
    canRequestPhone: typeof app.requestContact === 'function',
    credential: () => ({ kind: 'telegram', initData: app.initData }),
    startRoute: () => startParamToRoute(app.initDataUnsafe.start_param),
    preferredLanguage: () => app.initDataUnsafe.user?.language_code ?? null,
    ready() {
      app.ready();
      app.expand();
      app.setHeaderColor?.('#ffffff');
    },
    setBackButton(visible, onClick) {
      if (backHandler) app.BackButton.offClick(backHandler);
      backHandler = null;
      if (visible && onClick) {
        backHandler = onClick;
        app.BackButton.onClick(onClick);
        app.BackButton.show();
      } else {
        app.BackButton.hide();
      }
    },
    haptic(kind) {
      if (kind === 'light') app.HapticFeedback?.impactOccurred('light');
      else app.HapticFeedback?.notificationOccurred(kind);
    },
    openExternal: (url) => app.openLink(url),
    requestPhone: () =>
      new Promise((resolve) => (app.requestContact ? app.requestContact((ok) => resolve(ok)) : resolve(false))),
    requestNotifications: () =>
      new Promise((resolve) => (app.requestWriteAccess ? app.requestWriteAccess((ok) => resolve(ok)) : resolve(false))),
  };
}
